import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader 
} from '@/components/layout/MobileLayout';
import { mockEvents } from '@/lib/mockData';
import { formatCurrency, formatDate } from '@/lib/utils';

export function UserTicketSelection() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [selectedTicketType, setSelectedTicketType] = useState('');
  const [quantity, setQuantity] = useState(1);

  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];
  const selectedTicket = event.ticketTypes.find(t => t.id === selectedTicketType);
  const totalPrice = selectedTicket ? selectedTicket.price * quantity : 0;

  const handleCheckout = () => {
    navigate('/payment/processing');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="user-ticket-selection">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header */}
      <DesktopHeader />
      
      <main className="pt-14 lg:pt-20 pb-24 lg:pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Back button */}
          <Link to={`/events/${eventId}`} className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Event
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left: Event Summary */}
            <Card className="bg-card border-border overflow-hidden">
              <img 
                src={event.image} 
                alt={event.title}
                className="w-full aspect-video object-cover"
              />
              <div className="p-6">
                <h2 className="text-xl font-bold text-white mb-2">{event.title}</h2>
                <p className="text-muted-foreground">{formatDate(event.date)}</p>
                <p className="text-muted-foreground">{event.venue}, {event.city}</p>
              </div>
            </Card>

            {/* Right: Ticket Selection */}
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">Select Tickets</h1>
                <p className="text-muted-foreground">Choose your ticket type and quantity</p>
              </div>

              {/* Ticket Types */}
              <RadioGroup value={selectedTicketType} onValueChange={setSelectedTicketType}>
                <div className="space-y-3">
                  {event.ticketTypes.map(ticket => (
                    <Card 
                      key={ticket.id}
                      className={`p-4 cursor-pointer transition-all ${
                        selectedTicketType === ticket.id 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border bg-card hover:border-primary/50'
                      }`}
                      onClick={() => setSelectedTicketType(ticket.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value={ticket.id} id={ticket.id} />
                          <Label htmlFor={ticket.id} className="cursor-pointer">
                            <p className="font-semibold text-white">{ticket.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {ticket.available > 0 ? `${ticket.available} available` : 'Sold out'}
                            </p>
                          </Label>
                        </div>
                        <p className="text-lg font-bold text-primary">{formatCurrency(ticket.price)}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </RadioGroup>

              {/* Quantity */}
              {selectedTicketType && (
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">Quantity</span>
                  <div className="flex items-center gap-4">
                    <button 
                      className="w-10 h-10 rounded-lg bg-secondary border border-border flex items-center justify-center text-white hover:bg-primary/20 disabled:opacity-50"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-2xl font-bold text-white w-12 text-center">{quantity}</span>
                    <button 
                      className="w-10 h-10 rounded-lg bg-secondary border border-border flex items-center justify-center text-white hover:bg-primary/20"
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Order Summary */}
              {selectedTicketType && (
                <Card className="bg-secondary/50 border-border p-4">
                  <h3 className="text-white font-semibold mb-3">Order Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>{selectedTicket?.name} x {quantity}</span>
                      <span>{formatCurrency(totalPrice)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Service Fee</span>
                      <span>{formatCurrency(totalPrice * 0.1)}</span>
                    </div>
                    <div className="border-t border-border pt-2 mt-2">
                      <div className="flex justify-between text-white font-bold text-lg">
                        <span>Total</span>
                        <span>{formatCurrency(totalPrice * 1.1)}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Checkout Button */}
              <Button 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-lg font-semibold"
                disabled={!selectedTicketType}
                onClick={handleCheckout}
              >
                CHECKOUT
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

export default UserTicketSelection;
